import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { farmvestSyncService } from '../../../services/api';
import { fmtWhen, inr } from './kycLabels';

// The investor's details as last sent to FarmVest
// (GET /purchases/investors/{mobile}/farmvest-syncs). Investor, KYC and bank details
// are shown once, from the newest sync; orders and unit nominees are listed together.
// Identifiers arrive masked and documents as file names; this only shows them.

// Personal, KYC and address together (address last); bank on its own.
const PERSONAL_KEYS: [string, string][] = [
    ['first_name', 'First name'], ['last_name', 'Last name'], ['mobile', 'Mobile'], ['email', 'Email'], ['gender', 'Gender'], ['dob', 'Date of birth'], ['occupation', 'Occupation'],
    ['aadhar_card_number', 'Aadhaar number'], ['aadhar_card_front', 'Aadhaar front'], ['aadhar_card_back', 'Aadhaar back'], ['pan_card_number', 'PAN number'], ['pan_card_image', 'PAN image'],
    ['city', 'City'], ['state', 'State'], ['pincode', 'Pincode'],
    ['permanent_address', 'Permanent address'], ['current_address', 'Current address'], ['aadhar_address', 'Aadhaar address'],
];
const BANK_KEYS: [string, string][] = [
    ['bank_holder_name', 'Account holder'], ['bank_name', 'Bank name'], ['account_number', 'Account number'], ['ifsc_code', 'IFSC'], ['bank_image_url', 'Passbook'], ['cancelled_cheque', 'Cancelled cheque'],
];

const NOMINEE_KEYS: [string, string][] = [
    ['nominee_relation', 'Relation'], ['nominee_mobile', 'Mobile'], ['nominee_aadhar', 'Aadhaar'], ['nominee_pancard_number', 'PAN'],
    ['nominee_aadhar_front_image', 'Aadhaar front'], ['nominee_aadhar_back_image', 'Aadhaar back'], ['nominee_pancard_photo', 'PAN photo'],
];

// "…/animalkart%2Fstaging%2F924…%2Fkyc%2F1792587498814_sravani.jpg" -> "sravani.jpg"
const docName = (v: string) => {
    let s = v.replace(/^…\//, '');
    try { s = decodeURIComponent(s); } catch { /* keep as is */ }
    return (s.split('/').pop() || v).replace(/^\d{10,}_/, '');
};

const show = (v: any): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'string' && v.startsWith('…/')) return docName(v);
    return String(v);
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3 py-1.5 border-b border-slate-100">
        <span className="text-[12px] text-slate-500">{label}</span>
        <span className="text-[13px] font-semibold text-slate-900 break-words">{value}</span>
    </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="rounded-xl ring-1 ring-slate-200 p-4">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">{title}</div>
        {children}
    </div>
);

const Details: React.FC<{ rows: any[] }> = ({ rows }) => {
    const sentOf = (r: any) => (r.sentFields && typeof r.sentFields === 'object' ? r.sentFields : {});
    // Rows come newest first: the investor's details as FarmVest last received them.
    const latest = rows[0];
    const sent = sentOf(latest);
    const nominees = rows.flatMap(r => (Array.isArray(sentOf(r).nominees) ? sentOf(r).nominees : []))
        .sort((a: any, b: any) => String(a.unit_number).localeCompare(String(b.unit_number)));
    return (
        <>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-600">
                {latest.success
                    ? <span className="inline-flex items-center gap-1 font-bold text-emerald-800"><CheckCircle2 size={14} />Synced</span>
                    : <span className="inline-flex items-center gap-1 font-bold text-red-700"><XCircle size={14} />Last sync failed</span>}
                <span>Last sent {fmtWhen(latest.attemptedAt)}</span>
            </div>
            {!latest.success && latest.error && <p className="px-3 py-2 rounded-lg bg-red-50 text-[12px] text-red-800 break-words">{latest.error}</p>}

            <Section title="Personal, KYC & address">
                <div className="grid md:grid-cols-2 md:gap-x-6">
                    {PERSONAL_KEYS.map(([k, label]) => <Field key={k} label={label} value={show(sent[k])} />)}
                </div>
            </Section>

            <Section title="Bank">
                <div className="grid md:grid-cols-2 md:gap-x-6">
                    {BANK_KEYS.map(([k, label]) => <Field key={k} label={label} value={show(sent[k])} />)}
                </div>
            </Section>

            <Section title="Orders & nominees">
                <div className="text-[12px] font-bold text-slate-700 mb-1">Orders ({rows.length})</div>
                <div className="grid gap-3 md:grid-cols-2 mt-1">
                    {rows.map(r => {
                        const s = sentOf(r);
                        return (
                            <div key={r.id || r.orderId} className="rounded-lg bg-slate-50 p-3">
                                <div className="flex items-baseline justify-between gap-2 mb-1">
                                    <b className="font-mono text-[13px] text-slate-900">{r.orderId || s.animalkart_order_id}</b>
                                    <span className={`text-[11px] font-bold ${r.success ? 'text-emerald-800' : 'text-red-700'}`}>{r.success ? 'Synced' : 'Failed'}</span>
                                </div>
                                <Field label="Placed on" value={show(s.order_placed_date)} />
                                <Field label="Units" value={show(s.number_of_units)} />
                                <Field label="Unit cost" value={s.unit_cost != null ? inr(s.unit_cost) : '—'} />
                                <Field label="Total investment" value={s.total_investment_amount != null ? inr(s.total_investment_amount) : '—'} />
                                <Field label="Status" value={show(s.status)} />
                            </div>
                        );
                    })}
                </div>
                <div className="text-[12px] font-bold text-slate-700 mt-4 mb-1">Nominees ({nominees.length})</div>
                {nominees.length === 0 ? <p className="text-[13px] text-slate-400 py-1">No nominees sent yet.</p> : (
                    <div className="grid gap-3 md:grid-cols-2 mt-1">
                        {nominees.map((n: any, i: number) => (
                            <div key={n.unit_number || i} className="rounded-lg bg-slate-50 p-3">
                                <div className="flex items-baseline justify-between gap-2 mb-1">
                                    <b className="text-[13px] text-slate-900">{show(n.nominee_name)}</b>
                                    <span className="font-mono text-[11px] text-slate-500">{n.unit_number}</span>
                                </div>
                                {NOMINEE_KEYS.map(([k, label]) => <Field key={k} label={label} value={show(n[k])} />)}
                            </div>
                        ))}
                    </div>
                )}
            </Section>
        </>
    );
};

const FarmvestSyncModal: React.FC<{ mobile: string; onClose: () => void }> = ({ mobile, onClose }) => {
    const [rows, setRows] = useState<any[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await farmvestSyncService.list(mobile);
            if (cancelled) return;
            if (res.error) setError(res.error);
            else setRows(res.data?.rows || []);
        })();
        return () => { cancelled = true; };
    }, [mobile]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-[1000] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="fv-sync-title" onClick={onClose}>
            <div className="bg-white w-full sm:max-w-4xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
                    <h3 id="fv-sync-title" className="font-bold text-slate-900">FarmVest synced details</h3>
                    <button type="button" aria-label="Close" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-lg hover:bg-slate-100"><X size={16} /></button>
                </div>
                <div className="p-5 overflow-y-auto flex flex-col gap-3">
                    {error ? <p role="alert" className="text-sm font-semibold text-red-700">{error}</p>
                        : rows === null ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[#1B4332]" /></div>
                        : rows.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">Nothing has been sent to FarmVest for this investor yet.</p>
                        : <Details rows={rows} />}
                </div>
            </div>
        </div>
    );
};

export default FarmvestSyncModal;
